import { Link } from 'react-router-dom';
import type { Star } from '../api/practices.js';
import { usePractices } from '../api/use-practices.js';
import { PracticeStar } from './practice-star.js';
import { practiceWords } from './practice-words.js';
import { WaitNote } from './wait-note.js';

/**
 * The Practices page (board R2): totals, the streaks, Stars, Start a practice
 * and every practice newest first. The athlete's, a parent's for a child
 * (decision 6) and Coach Matt's (board R5) are this one page; a coach has no
 * Start a practice.
 */
export function PracticesPage(props: {
  athleteClientId: string | null;
  title: string;
  back: { to: string; label: string };
  start: string | null;
  practiceLink: (practiceId: string) => string;
}) {
  const state = usePractices(props.athleteClientId);
  return (
    <>
      <div className="page-head">
        <Link className="back" to={props.back.to} aria-label={props.back.label}>
          ‹
        </Link>
        <div>
          <h2>{props.title}</h2>
        </div>
      </div>
      {state.status === 'loading' ? <WaitNote className="empty">Loading…</WaitNote> : null}
      {state.status === 'failed' ? <p className="warn-note">{state.message}</p> : null}
      {state.status === 'ready' ? (
        <>
          <div className="practice-figures practice-figures-3">
            <Figure label="Practices" value={String(state.summary.practice_count)} />
            <Figure label="Swings" value={String(state.summary.swing_count)} />
            <Figure label="Time" value={practiceWords.time(state.summary.time_seconds)} />
          </div>
          <div className="practice-figures">
            <Figure label="Current streak" value={practiceWords.days(state.summary.current_streak)} star={state.summary.current_star} />
            <Figure label="Best streak" value={practiceWords.days(state.summary.best_streak)} star={state.summary.best_star} />
          </div>
          <div className="practice-stars">
            <span className="practice-label">Stars</span>
            <span className="practice-star-counts">
              {(['bronze', 'silver', 'gold', 'diamond'] as const).map((star) => (
                <span key={star}>
                  <PracticeStar star={star} size={22} label={STARS[star]} />
                  {state.summary.stars[star]}
                </span>
              ))}
            </span>
          </div>
          {props.start ? (
            <Link className="primary" to={props.start}>
              Start a practice
            </Link>
          ) : null}
          {state.practices.map((practice) => (
            <Link key={practice.id} className="hour-row" to={props.practiceLink(practice.id)}>
              <span className="hour-row-text">
                <span className="name">{practiceWords.day(practice.practice_date)}</span>
                <span className="detail">
                  {[practiceWords.swings(practice.swing_count), practiceWords.time(practice.time_seconds), practiceWords.typeList(practice.types)]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="chevron" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </>
      ) : null}
    </>
  );
}

const STARS: Record<Star, string> = { bronze: 'Bronze stars', silver: 'Silver stars', gold: 'Gold stars', diamond: 'Diamonds' };

function Figure({ label, value, star }: { label: string; value: string; star?: Star | null }) {
  return (
    <div>
      <span className="practice-label">{label}</span>
      <span className="practice-figure">
        {value}
        {star ? <PracticeStar star={star} size={24} /> : null}
      </span>
    </div>
  );
}
